/**
 * Telemetry — Lightweight event tracking for Saige analytics.
 *
 * Fire-and-forget event logger. Posts to /internal/analytics/event.
 * Uses navigator.sendBeacon for non-blocking delivery.
 * Never throws — fails silently.
 *
 * V3 additions:
 * - Session ID management (survives SPA navigation, resets on tab close)
 * - User context enrichment (user_uuid, district_id, campus_id)
 * - Route and feature_area support
 * - Deployment version tagging
 */

// ── Endpoint ─────────────────────────────────────────────────────────────────
// Must match the FastAPI router prefix + POST /event handler.
// In production the frontend is served from the same origin as the backend,
// so a relative path works. When VITE_API_URL is set (e.g. Vercel → Cloud Run),
// the axios baseURL handles it, but sendBeacon needs an absolute URL.

function _getEndpoint() {
    // Try to derive from the same env var axios uses
    const base = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
    return `${base}/internal/analytics/event`;
}

// ── Session management ───────────────────────────────────────────────────────
// A lightweight per-tab session ID. Resets when the tab is opened fresh.

let _sessionId = null;

function _getSessionId() {
    if (!_sessionId) {
        // Check sessionStorage first (survives SPA nav, resets on tab close)
        try {
            _sessionId = sessionStorage.getItem('saige_session_id');
            if (!_sessionId) {
                _sessionId = _generateId();
                sessionStorage.setItem('saige_session_id', _sessionId);
            }
        } catch {
            _sessionId = _generateId();
        }
    }
    return _sessionId;
}

function _generateId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── User context ─────────────────────────────────────────────────────────────
// Reads from localStorage/profile. Never throws.

function _getUserContext() {
    try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        return {
            user_uuid: profile?.id || profile?.user_id || undefined,
            district_id: profile?.district_id || undefined,
            campus_id: profile?.campus_id || undefined,
        };
    } catch {
        return {};
    }
}

// ── Session-aware timer for time_to_first_lesson ────────────────────────────
let _sessionStart = Date.now();
let _firstLessonTracked = false;

/**
 * Fire a telemetry event. Non-blocking, fail-silent.
 *
 * @param {string} name  – Event name (e.g. 'lesson_generated', 'view_enter')
 * @param {object} props – Additional properties to include
 */
export function trackEvent(name, props = {}) {
    try {
        const userCtx = _getUserContext();
        const payload = JSON.stringify({
            event: name,
            session_id: _getSessionId(),
            route: typeof window !== 'undefined' ? window.location.pathname : undefined,
            ...userCtx,
            ...props,          // caller props override defaults
            ts: Date.now(),
        });

        const endpoint = _getEndpoint();

        // Prefer sendBeacon (non-blocking, survives page unload).
        // IMPORTANT: sendBeacon(url, string) sends text/plain, which FastAPI
        // rejects as 422. Wrapping in a Blob with application/json MIME type
        // ensures the backend can parse the JSON body correctly.
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(endpoint, blob);
        } else {
            // Fallback: fire-and-forget fetch
            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true,
            }).catch(() => { /* intentional silent fail */ });
        }
    } catch {
        // Never throw from telemetry
    }
}

/**
 * Track time_to_first_lesson (only fires once per session).
 */
export function trackFirstLesson() {
    if (_firstLessonTracked) return;
    _firstLessonTracked = true;
    const duration = Date.now() - _sessionStart;
    trackEvent('time_to_first_lesson', { duration_ms: duration });
}

/**
 * Reset session timer (e.g. after login).
 */
export function resetSessionTimer() {
    _sessionStart = Date.now();
    _firstLessonTracked = false;
}

// ── Session lifecycle ────────────────────────────────────────────────────────

let _sessionStarted = false;

/**
 * Call once when the app mounts to emit session_started.
 * Also sets up beforeunload to emit session_ended.
 */
export function initSessionTracking() {
    if (_sessionStarted) return;
    _sessionStarted = true;

    trackEvent('session_started');

    // session_ended on tab close / navigate away
    if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
            trackEvent('session_ended', {
                session_duration_ms: Date.now() - _sessionStart,
            });
        });
    }
}

// ── View lifecycle ───────────────────────────────────────────────────────────

let _currentView = null;
let _viewEnterTs = null;

/**
 * Track a view transition. Emits view_exit for the previous view (if any)
 * and view_enter for the new view. Call this when the top-level view changes.
 *
 * @param {string} viewName – e.g. 'dashboard', 'admin-workspace', 'landing'
 */
export function trackViewChange(viewName) {
    if (viewName === _currentView) return; // no-op on same view

    const now = Date.now();

    // Exit previous view
    if (_currentView && _viewEnterTs) {
        trackEvent('view_exit', {
            view: _currentView,
            duration_ms: now - _viewEnterTs,
        });
    }

    // Enter new view
    _currentView = viewName;
    _viewEnterTs = now;
    trackEvent('view_enter', { view: viewName });
}

// ── Feature area lifecycle ───────────────────────────────────────────────────

const _activeFeatureAreas = new Map(); // feature_area → enter_ts

/**
 * Emit feature_area_entered. Returns a cleanup function that emits
 * feature_area_exited. Designed to be called in useEffect:
 *
 *   useEffect(() => trackFeatureArea('lesson_planner'), []);
 *
 * @param {string} area – Feature area name
 * @returns {() => void} cleanup function for useEffect
 */
export function trackFeatureArea(area) {
    if (_activeFeatureAreas.has(area)) return () => {}; // already tracked

    const enterTs = Date.now();
    _activeFeatureAreas.set(area, enterTs);
    trackEvent('feature_area_entered', { feature_area: area });

    return () => {
        const storedTs = _activeFeatureAreas.get(area);
        _activeFeatureAreas.delete(area);
        trackEvent('feature_area_exited', {
            feature_area: area,
            duration_ms: Date.now() - (storedTs || enterTs),
        });
    };
}

// ── Tool lifecycle ───────────────────────────────────────────────────────────

/**
 * Emit tool_opened. Returns a cleanup function that emits tool_closed.
 *
 * @param {string} toolName – e.g. 'classroom_board', 'quiz_modal'
 * @returns {() => void} cleanup function
 */
export function trackToolOpen(toolName) {
    trackEvent('tool_opened', { tool: toolName });
    const openTs = Date.now();
    return () => {
        trackEvent('tool_closed', {
            tool: toolName,
            duration_ms: Date.now() - openTs,
        });
    };
}

// ── Generation lifecycle ─────────────────────────────────────────────────────

/**
 * Wrap an async generation call with start/complete/fail events.
 *
 * @param {string} generationType – e.g. 'lesson', 'slides', 'quiz'
 * @param {object} meta – Additional metadata (subject, date, etc)
 * @param {() => Promise} fn – The async function to execute
 * @returns {Promise} The result of fn()
 */
export async function trackGeneration(generationType, meta, fn) {
    const requestId = _generateId();
    trackEvent('generation_started', {
        generation_type: generationType,
        request_id: requestId,
        ...meta,
    });

    try {
        const result = await fn();
        trackEvent('generation_completed', {
            generation_type: generationType,
            request_id: requestId,
            ...meta,
        });
        return result;
    } catch (err) {
        trackEvent('generation_failed', {
            generation_type: generationType,
            request_id: requestId,
            error: err?.message || String(err),
            ...meta,
        });
        throw err; // re-throw so callers see the error
    }
}


// ── Chat-specific telemetry (Phase 1A) ──────────────────────────────────────

/**
 * Track a chat-specific event with plan_tier auto-included.
 * Convenience wrapper for common chat analytics events.
 *
 * @param {string} name  – e.g. 'chat_sent', 'chat_response_received', 'chat_copy'
 * @param {object} props – Additional properties
 */
export function trackChatEvent(name, props = {}) {
    try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        const plan_tier = profile?.plan_tier || profile?.subscription || 'Free';
        trackEvent(name, { plan_tier, ...props });
    } catch {
        trackEvent(name, props);
    }
}


// ── Audio-specific telemetry (Phase 2A) ──────────────────────────────────────

/**
 * Track an audio/voiceover event with plan_tier + feature_type auto-included.
 * Convenience wrapper for the audio funnel analytics pipeline.
 *
 * @param {string} name  – e.g. 'audio_voice_created', 'audio_generation_completed'
 * @param {object} props – Additional properties (preset, lesson_document_id, etc.)
 */
export function trackAudioEvent(name, props = {}) {
    try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        const plan_tier = profile?.plan_tier || profile?.subscription || 'Free';
        trackEvent(name, { feature_type: 'audio', plan_tier, ...props });
    } catch {
        trackEvent(name, { feature_type: 'audio', ...props });
    }
}


// ── Conversion / monetization telemetry (Phase 3A) ──────────────────────────

/**
 * Track a conversion-related event with plan_tier auto-included.
 * Use for upgrade nudges, pricing page opens, checkout starts, etc.
 * NOT for audio-specific or chat-specific events — use trackAudioEvent or
 * trackChatEvent for those.
 *
 * @param {string} name  – e.g. 'upgrade_nudge_shown', 'pricing_page_opened'
 * @param {object} props – Additional properties (source, nudge_type, etc.)
 */
export function trackConversionEvent(name, props = {}) {
    try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        const plan_tier = profile?.plan_tier || profile?.subscription || 'Free';
        trackEvent(name, { feature_type: 'conversion', plan_tier, ...props });
    } catch {
        trackEvent(name, { feature_type: 'conversion', ...props });
    }
}


// ── Upgrade nudge frequency capping (Phase 3A) ─────────────────────────────

const NUDGE_SESSION_KEY = 'saige_nudge_shown';

/**
 * Check if a nudge of the given type has already been shown this session.
 * @param {string} nudgeType – e.g. 'post_lesson', 'locked_preset'
 * @returns {boolean}
 */
export function hasNudgedThisSession(nudgeType) {
    try {
        const shown = JSON.parse(sessionStorage.getItem(NUDGE_SESSION_KEY) || '{}');
        return !!shown[nudgeType];
    } catch {
        return false;
    }
}

/**
 * Mark a nudge type as shown for this session.
 * @param {string} nudgeType
 */
export function markNudgeShown(nudgeType) {
    try {
        const shown = JSON.parse(sessionStorage.getItem(NUDGE_SESSION_KEY) || '{}');
        shown[nudgeType] = Date.now();
        sessionStorage.setItem(NUDGE_SESSION_KEY, JSON.stringify(shown));
    } catch {
        // silent
    }
}


// ── Premium adoption analytics (Phase 3B) ───────────────────────────────────

const UPGRADE_TS_KEY = 'saige_upgrade_timestamp';
const FIRST_ACTION_KEY = 'saige_first_premium_actions';

/**
 * Record when the user completed an upgrade. Called once on post-checkout return.
 * Used to compute "first action after upgrade" and time-to-first-use metrics.
 */
export function recordUpgradeTimestamp() {
    try {
        localStorage.setItem(UPGRADE_TS_KEY, String(Date.now()));
    } catch { /* silent */ }
}

/**
 * Track a premium adoption event. Like trackConversionEvent but with
 * feature_type='premium_adoption' and automatic "first action" dedup.
 *
 * @param {string} name  – e.g. 'first_enhanced_lesson_generated'
 * @param {object} props – Additional properties
 */
export function trackPremiumAdoptionEvent(name, props = {}) {
    try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        const plan_tier = profile?.plan_tier || profile?.subscription || 'Free';
        const upgradeTs = localStorage.getItem(UPGRADE_TS_KEY);

        const extraProps = {
            feature_type: 'premium_adoption',
            plan_tier,
            ...props,
        };

        if (upgradeTs) {
            const msSinceUpgrade = Date.now() - parseInt(upgradeTs, 10);
            extraProps.hours_since_upgrade = Math.round(msSinceUpgrade / 3600000 * 10) / 10;
        }

        trackEvent(name, extraProps);
    } catch {
        trackEvent(name, { feature_type: 'premium_adoption', ...props });
    }
}

/**
 * Check if a "first premium action" of the given type has already been recorded.
 * Used to fire events like `first_enhanced_lesson_generated` only once ever.
 *
 * @param {string} actionType
 * @returns {boolean}
 */
export function hasRecordedFirstAction(actionType) {
    try {
        const actions = JSON.parse(localStorage.getItem(FIRST_ACTION_KEY) || '{}');
        return !!actions[actionType];
    } catch {
        return false;
    }
}

/**
 * Mark a "first premium action" as recorded (persistent across sessions).
 * @param {string} actionType
 */
export function markFirstAction(actionType) {
    try {
        const actions = JSON.parse(localStorage.getItem(FIRST_ACTION_KEY) || '{}');
        actions[actionType] = Date.now();
        localStorage.setItem(FIRST_ACTION_KEY, JSON.stringify(actions));
    } catch { /* silent */ }
}
